import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import mapboxgl from "mapbox-gl";
import { useMap } from "../../contexts/MapContext";
import { useUrbanAnimation } from "../../contexts/UrbanAnimationContext";
import { useSimulation } from "../../contexts/SimulationContext";
import { useFire3DData } from "../../api/simulations";
import {
  FUEL_FILL_COLOR,
  FUEL_FILL_OPACITY,
  DIM_FILL_COLOR,
  DIM_FILL_OPACITY,
} from "../../constants/fuelLoadPaint";
import { RECALC_GREY, RECALC_GREY_MIX } from "../../constants/recalcPaint";
import { SOURCE_LAYER, clamp } from "./fuelClassification";
import { useFuel3DOptions } from "../../hooks/use3DFuelOptions";
import type {
  Bbox,
  BucketResult,
  CrownSet,
  FireGridData,
  GroundStandInput,
  PlantRequest,
  PlantResult,
  PolySet,
  StandInput,
} from "./fuel3d/types";
import { CROWN_FIRE_FROM_GRID, FIRE_NEVER } from "./fuel3d/types";
import { useParams } from "react-router-dom";
import { CROWN_ROLE_INTERIOR, useCrowns } from "../../api/crowns";
import { runPlanting } from "./fuel3d/plantingCore";
import { buildElevGrid, elevAt, type ElevGrid } from "./fuel3d/elevGrid";
import { getFuelFeatureInfo } from "./fuel3d/parseCache";
import { p3dGauge, p3dRecord, p3dSpan, perfEnabled } from "./fuel3d/perf";
import PerfHud from "./fuel3d/PerfHud";

/**
 * 3D vegetation for the Fuel Load layer. Each sim fuel model emits a different
 * set of per-pixel attributes into the MVT's `fuelCharacteristics` /
 * `strataBreakdown`; the MVT carries NO model-class id, so the vegetation type
 * is inferred from which characteristic keys are present (see `classifyStand`)
 * and rendered with a matching archetype (see fuelClassification.ts).
 *
 * Rendering is a Mapbox custom layer (three.js sharing the map's WebGL2
 * context). Plants are scattered on a deterministic world-space grid so the
 * same plant appears at the same spot on every rebuild, and duplicates from
 * vector-tile buffer overlap collapse onto the same grid key.
 *
 * Performance architecture (issue #276):
 * - The planting loop (millions of candidate cells) runs in a Web Worker
 *   (fuel3d/plantingCore + plantingWorker) and returns flat instance buffers,
 *   so a camera settle costs the main thread only feature query/classify
 *   (cached per feature id) + attribute upload.
 * - Each generation is built into a hidden group and swapped in when complete
 *   (double-buffer) — the previous forest stays visible during the rebuild.
 * - The fire animation is fully GPU-driven: per-crown arrival/reached are
 *   baked as instanced attributes and a single `uTime` uniform advances the
 *   burn — a playback tick is one uniform write + repaint, no instance
 *   uploads and no shadow-map re-render.
 */

interface FuelLoad3DTreesProps {
  /** Source id of the fuel-load vector source (same as the fill layer id). */
  sourceId: string;
  active: boolean;
  /**
   * Whether this layer owns the 2D fuel fill and should mute it while trees are
   * shown. The standalone 3D Fuel layer has only an invisible tile-loading fill,
   * so it passes false to leave that fill untouched.
   */
  dimFill?: boolean;
  /**
   * Render the whole stand flat grey — set while a fuel recalculation is in
   * flight and the trees are showing retained (stale) tiles, so the display
   * reads as "still here, but out of date" instead of vanishing.
   */
  greyed?: boolean;
  /**
   * Which parts to draw, overriding the device-wide toggles from
   * use3DFuelOptions (story maps store the choice per layer entry). Fire
   * keeps following the project page's animation contexts.
   */
  show?: { canopy: boolean; stems: boolean; surface: boolean; grass: boolean };
  /**
   * Project whose imagery-placed crowns to draw. Absent = the route's
   * :projectId (project page); null = plant from the fuel tiles only
   * (anonymous story readers have no session for the crowns endpoint).
   */
  projectId?: string | null;
  /**
   * Called after each planting pass with whether it was complete (every
   * tree had terrain under it) — the story print capture waits on this
   * before reading the canvas back.
   */
  onPlanted?: (complete: boolean) => void;
}

/** Hard cap on rendered trees per rebuild (density backs off before hitting it). */
const MAX_TREES = 300000;
/**
 * Combined cap on ground-cover instances (litter + near-surface shrubs + logs)
 * per rebuild. Density backs off before this is hit, the same way trees do.
 */
const MAX_GROUND = 500000;
/**
 * Ground cover (fine litter, low shrubs, fallen logs) only renders at this zoom
 * and in. Below it the elements are sub-pixel and the fine planting grid would
 * cover far more ground than the budget allows; trees render at every zoom the
 * fuel tiles load at (the lattice pitch and a screen-size exaggeration scale
 * with zoom, so zoomed-out views stay bounded and readable).
 */
const GROUND_MIN_ZOOM = 16;
/** A nominal tree this tall (m) is held to at least TREE_MIN_PX on screen. */
const NOMINAL_TREE_M = 15;
const TREE_MIN_PX = 10;
/** Terrain source id added in Map.tsx — its tile loads gate tree elevation. */
const DEM_SOURCE_ID = "mapbox-dem";
/**
 * Tree bases and the shadow receiver sample terrain from one coarse grid built
 * once per rebuild, then bilinearly interpolated (see fuel3d/elevGrid).
 */
const ELEV_GRID_N = 96;
/** Shadow-receiver tessellation (the elevation grid does the heavy lifting). */
const RECEIVER_SEGMENTS = 96;
/**
 * Fraction of instances drawn while the camera is moving (restored on settle).
 * Non-zero so the forest persists (thinned) through a move — the double-buffered
 * rebuild means there is no longer a settle stall to hide.
 */
const MOVING_FRAC = 1;
/**
 * Whole-layer opacity (1 = solid). Below 1 the basemap reads through the
 * canopy — the vegetation becomes a translucent overlay rather than solid
 * cover. Blending is depth-writing (cheap): each plant composites correctly
 * over the map, while overlaps BETWEEN plants resolve by draw order, which on
 * dense foliage reads fine and costs nothing extra.
 * Live-tweakable without a code change: set localStorage
 * "fisci.fuel3d_opacity" (0.05–1) in the console and nudge the map — the
 * materials are rebuilt on every settle.
 */
const LAYER_OPACITY = 0.55;

function layerOpacity(): number {
  try {
    const v = parseFloat(localStorage.getItem("fisci.fuel3d_opacity") ?? "");
    if (Number.isFinite(v)) return clamp(v, 0.05, 1);
  } catch {
    /* storage unavailable */
  }
  return LAYER_OPACITY;
}

// ---------------------------------------------------------------------------
// 3D fire animation. When a fire simulation is animating the whole burn is
// fetched once as a sparse list of cells — [lng, lat, arrivalSeconds,
// peakFlameHeightM] — rasterised into a per-rebuild grid, and baked into
// per-crown instanced attributes. Playback is then entirely GPU-side, driven
// by one `uTime` uniform: a crown over active fire lights up with a warm
// firelight underglow, and collapses once the front passes if the cell's peak
// flames reached its crown.
// ---------------------------------------------------------------------------
const FIRE_GRID_N = 128; // arrival/peak-flame grid resolution over the view
/** A cell glows with active firelight for this many sim-seconds after the front
 *  arrives, then settles to a residual smoulder. */
const FLAME_FADE_SECONDS = 360;
/** Close passes — fills gaps between sparse cells without growing the footprint. */
const FIRE_GRID_DILATE = 2;

/** One burnt cell projected to mercator, with its arrival frame + peak flame. */
interface FireCell {
  mx: number;
  my: number;
  arrival: number;
  peak: number;
}

/** Nearest-cell fire sample at a mercator point (grid is coarse; no interp). */
function fireSampleIdx(g: FireGridData, mx: number, my: number): number {
  const ix = clamp(Math.round((mx - g.x0) / g.dx), 0, g.n - 1);
  const iy = clamp(Math.round((my - g.y0) / g.dy), 0, g.n - 1);
  return iy * g.n + ix;
}

// ---------------------------------------------------------------------------
// Unit geometries with the origin at the base so scale.y maps to height.
// ---------------------------------------------------------------------------
const trunkGeo = new THREE.CylinderGeometry(0.5, 0.65, 1, 6).translate(0, 0.5, 0);
const coneGeo = new THREE.ConeGeometry(0.5, 1, 8).translate(0, 0.5, 0);
const blobGeo = new THREE.SphereGeometry(0.5, 8, 6).translate(0, 0.5, 0);
// Far-field crown LOD — the same silhouettes at a fraction of the vertex cost;
// beyond the far distance a crown is a few pixels, the facets are invisible.
const coneFarGeo = new THREE.ConeGeometry(0.5, 1, 4).translate(0, 0.5, 0);
const blobFarGeo = new THREE.SphereGeometry(0.5, 5, 3).translate(0, 0.5, 0);
// Litter patch — a low-poly disc lying flat on the ground (normal up), unit
// radius so scale.x / scale.z map to its footprint.
const litterGeo = new THREE.CircleGeometry(0.5, 6).rotateX(-Math.PI / 2);
// Fallen log — a unit cylinder laid horizontal along its local X axis.
const logGeo = new THREE.CylinderGeometry(0.5, 0.5, 1, 6).rotateZ(Math.PI / 2);
// Grass tuft — a clump of thin tapered blades (solid 3-sided cones) splayed
// around a common base at the origin.
const grassGeo = (() => {
  const blades: THREE.BufferGeometry[] = [];
  const N = 5;
  for (let i = 0; i < N; i++) {
    const ang = (i / N) * Math.PI * 2 + i * 0.6;
    const lean = 0.18 + 0.16 * (i % 2);
    blades.push(
      new THREE.ConeGeometry(0.05, 1, 3)
        .translate(0, 0.5, 0) // base at origin
        .rotateZ(lean) // tip away from vertical
        .rotateY(ang) // splay around the clump
        .translate(Math.cos(ang) * 0.05, 0, Math.sin(ang) * 0.05)
    );
  }
  const merged = mergeGeometries(blades, false);
  blades.forEach((b) => b.dispose());
  return merged ?? new THREE.ConeGeometry(0.05, 1, 3).translate(0, 0.5, 0);
})();

// ---------------------------------------------------------------------------
// Feature geometry → flat mercator PolySet (worker-transferable)
// ---------------------------------------------------------------------------
const lngToMercX = (lng: number) => (180 + lng) / 360;
const latToMercY = (lat: number) =>
  (180 - (180 / Math.PI) * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360))) /
  360;

/** Flatten a (Multi)Polygon into a PolySet; returns null when degenerate. */
function geometryToPolySet(geometry: GeoJSON.Geometry): PolySet | null {
  const polys: GeoJSON.Position[][][] =
    geometry.type === "Polygon"
      ? [geometry.coordinates]
      : geometry.type === "MultiPolygon"
      ? geometry.coordinates
      : [];
  if (polys.length === 0) return null;
  let nPoints = 0;
  let nRings = 0;
  for (const rings of polys) {
    nRings += rings.length;
    for (const ring of rings) nPoints += ring.length;
  }
  if (nPoints === 0) return null;
  const coords = new Float64Array(nPoints * 2);
  const ringStarts = new Uint32Array(nRings + 1);
  const polyStarts = new Uint32Array(polys.length + 1);
  const bboxes = new Float64Array(polys.length * 4);
  let pt = 0;
  let ring = 0;
  for (let p = 0; p < polys.length; p++) {
    polyStarts[p] = ring;
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (const r of polys[p]) {
      ringStarts[ring++] = pt;
      for (const pos of r) {
        const x = lngToMercX(pos[0]);
        const y = latToMercY(pos[1]);
        coords[pt * 2] = x;
        coords[pt * 2 + 1] = y;
        pt++;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
    bboxes[p * 4] = minX;
    bboxes[p * 4 + 1] = minY;
    bboxes[p * 4 + 2] = maxX;
    bboxes[p * 4 + 3] = maxY;
  }
  polyStarts[polys.length] = ring;
  ringStarts[nRings] = pt;
  return { coords, ringStarts, polyStarts, bboxes };
}

/** The raw vector-tile feature mapbox's query results wrap (mapbox-gl ≥ v1). */
interface RawTileFeature {
  type: number;
  extent: number;
  loadGeometry(): Array<Array<{ x: number; y: number }>>;
}
interface TileBackedFeature {
  _vectorTileFeature?: RawTileFeature;
  _x?: number;
  _y?: number;
  _z?: number;
}

/** Signed ring area in tile space — mapbox's classifyRings convention. */
function tileRingArea(ring: Array<{ x: number; y: number }>): number {
  let a = 0;
  for (let i = 0, n = ring.length, j = n - 1; i < n; j = i++) {
    const p = ring[i];
    const q = ring[j];
    a += (q.x - p.x) * (p.y + q.y);
  }
  return a;
}

/**
 * Flatten a query feature straight from its raw tile geometry. `feature.geometry`
 * is a lazy getter that runs mapbox's toGeoJSON — an atan/exp per vertex — and
 * we then projected every lng/lat back to mercator with a log/tan: two
 * transcendental projections per vertex, on the main thread, for tens of
 * thousands of staircase vertices per settle on a large project. Tile
 * coordinates map to mercator linearly (mx = (tx + px/extent) / 2^z), which is
 * exactly what that round trip computes, minus the trig. Rings are grouped into
 * member polygons the way toGeoJSON does (first non-degenerate ring fixes the
 * outer winding; same-winding rings start a polygon, others are holes). Falls
 * back to the GeoJSON path if the feature isn't tile-backed.
 */
function featureToPolySet(feature: GeoJSON.Feature): PolySet | null {
  const f = feature as unknown as TileBackedFeature;
  const vt = f._vectorTileFeature;
  if (
    !vt ||
    vt.type !== 3 ||
    typeof vt.loadGeometry !== "function" ||
    !Number.isFinite(f._x) ||
    !Number.isFinite(f._y) ||
    !Number.isFinite(f._z)
  ) {
    return geometryToPolySet(feature.geometry);
  }
  let rings: Array<Array<{ x: number; y: number }>>;
  try {
    rings = vt.loadGeometry();
  } catch {
    return geometryToPolySet(feature.geometry);
  }
  let nPoints = 0;
  for (const r of rings) nPoints += r.length;
  if (nPoints === 0) return null;
  const extent = vt.extent || 4096;
  const k = 1 / (extent * Math.pow(2, f._z!));
  const ox = f._x! * extent;
  const oy = f._y! * extent;
  const coords = new Float64Array(nPoints * 2);
  const ringStarts = new Uint32Array(rings.length + 1);
  const polyStarts: number[] = [];
  const bboxes: number[] = [];
  let pt = 0;
  let ring = 0;
  let outerCcw: boolean | undefined;
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const r of rings) {
    const area = tileRingArea(r);
    if (area === 0) continue; // degenerate — toGeoJSON drops these too
    if (outerCcw === undefined) outerCcw = area < 0;
    if (outerCcw === area < 0) {
      // Outer ring → new member polygon.
      if (polyStarts.length > 0) bboxes.push(minX, minY, maxX, maxY);
      polyStarts.push(ring);
      minX = minY = Infinity;
      maxX = maxY = -Infinity;
    }
    ringStarts[ring++] = pt;
    for (const p of r) {
      const x = (p.x + ox) * k;
      const y = (p.y + oy) * k;
      coords[pt * 2] = x;
      coords[pt * 2 + 1] = y;
      pt++;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  if (polyStarts.length === 0) return null;
  bboxes.push(minX, minY, maxX, maxY);
  ringStarts[ring] = pt;
  polyStarts.push(ring);
  return {
    coords: pt * 2 === coords.length ? coords : coords.slice(0, pt * 2),
    ringStarts: ring + 1 === ringStarts.length ? ringStarts : ringStarts.slice(0, ring + 1),
    polyStarts: Uint32Array.from(polyStarts),
    bboxes: Float64Array.from(bboxes),
  };
}

// ---------------------------------------------------------------------------
// Custom layer
// ---------------------------------------------------------------------------
class FuelLoadTreeLayer implements mapboxgl.CustomLayerInterface {
  readonly id: string;
  readonly type = "custom" as const;
  readonly renderingMode = "3d" as const;

  private sourceId: string;
  private map: mapboxgl.Map | null = null;
  private renderer: THREE.WebGLRenderer | null = null;
  private scene: THREE.Scene | null = null;
  private camera: THREE.Camera | null = null;
  /** Scene-level container; the current generation's meshes live in `batch`. */
  private group: THREE.Group | null = null;
  /** The visible generation — replaced wholesale on rebuild (double-buffer). */
  private batch: THREE.Group | null = null;
  private sun: THREE.DirectionalLight | null = null;
  /** Invisible terrain-following mesh that receives the trees' shadows. */
  private shadowGround: THREE.Mesh | null = null;
  /** Mercator anchor of the scene origin; tree positions are metres from here. */
  private ref: { x: number; y: number; scale: number } | null = null;
  /** Anchor matrix (mercator→scene metres); updated on batch swap only. */
  private anchor = new THREE.Matrix4();
  private scratch = new THREE.Matrix4();
  private capWarned = false;
  /** True while the shadow map uses the high tier (PCFSoft @ 2048). */
  private shadowSoft = true;

  // --- rebuild pipeline ----------------------------------------------------
  /** Monotonic build generation — stale worker results are discarded. */
  private buildGen = 0;
  private worker: Worker | null = null;
  private workerBroken = false;
  private pending = new Map<number, (r: PlantResult | null) => void>();
  /**
   * The worker runs requests strictly in order, so a request posted behind a
   * long plant would be computed in full and then discarded as stale. While
   * it is busy only the newest request is kept, and posted when it frees.
   */
  private workerBusy = false;
  private queued: {
    req: PlantRequest;
    resolve: (r: PlantResult | null) => void;
  } | null = null;
  /** Movement-LOD state, applied to freshly swapped batches too. */
  private moving = false;
  private lastInfoSample = 0;

  // --- 3D fire animation (GPU-driven) --------------------------------------
  /** Whole burn, fetched once and projected to mercator (null = no fire). */
  private fireCells: FireCell[] | null = null;
  /** The project's imagery-placed crowns (null = none); drawn by the worker
   *  alongside the lattice, see PlantRequest.crowns. */
  private crownSet: CrownSet | null = null;
  /** Current sim time (seconds since ignition) — drives the crown shader. */
  private uTime = { value: 0 };
  /** Fire toggle from the 3D Fuel menu, as a shader uniform. */
  private uFireOn = { value: 1 };
  /** Shared uniform scaling the canopy firelight underglow (held steady). */
  private glowUniform = { value: 1 };
  private trunkMesh: THREE.InstancedMesh | null = null;
  /** Crown meshes (near+far cones/blobs) — the canopy visibility set. */
  private crownMeshes: THREE.InstancedMesh[] = [];
  /** When false, the 2D fuel fill is left alone (standalone 3D layer). */
  private manageFill = true;
  /** Part visibility from the 3D Fuel menu (crowns / trunks / flames). */
  private showCanopy = true;
  private showStems = true;
  private showFire = true;
  /** Ground-cover visibility. `showSurface` = litter + shrubs + logs; grass separate. */
  private showSurface = true;
  private showGrass = true;
  /** Grey the whole stand (stale data shown during a fuel recalculation). */
  private greyed = false;
  /** Layer opacity sampled at rebuild time (materials are per-generation). */
  private buildOpacity = LAYER_OPACITY;

  // --- ground cover (surface/near-surface layer) ---------------------------
  private litterMesh: THREE.InstancedMesh | null = null;
  private logMesh: THREE.InstancedMesh | null = null;
  private nsShrubMesh: THREE.InstancedMesh | null = null;
  private grassMesh: THREE.InstancedMesh | null = null;

  constructor(id: string, sourceId: string) {
    this.id = id;
    this.sourceId = sourceId;
  }

  /** Toggle whether this layer mutes the 2D fuel fill while trees are shown. */
  setManageFill(v: boolean) {
    this.manageFill = v;
  }

  /** Record which parts to show (applied on the next rebuild). */
  setShowOptions(
    canopy: boolean,
    stems: boolean,
    fire: boolean,
    surface: boolean,
    grass: boolean
  ) {
    this.showCanopy = canopy;
    this.showStems = stems;
    this.showFire = fire;
    this.showSurface = surface;
    this.showGrass = grass;
  }

  /**
   * Grey out / restore the stand without a re-plant. Greying pulls every
   * per-instance colour toward grey in place (a flat grey material made the
   * stand an indistinguishable mass — mixing keeps species/fuel tints faintly
   * readable while clearly stale); the originals are parked in userData and
   * written back on restore. New meshes baked while greyed are covered by the
   * applyGreyed() call after the batch swap.
   */
  setGreyed(value: boolean) {
    if (this.greyed === value) return;
    this.greyed = value;
    this.applyGreyed();
    if (this.renderer) this.renderer.shadowMap.needsUpdate = true;
    this.map?.triggerRepaint();
  }

  private applyGreyed() {
    if (!this.batch) return;
    const grey = new THREE.Color(RECALC_GREY);
    const c = new THREE.Color();
    for (const child of this.batch.children) {
      const mesh = child as THREE.InstancedMesh;
      if (!mesh.isInstancedMesh || !mesh.instanceColor) continue;
      const attr = mesh.instanceColor;
      const colors = attr.array as Float32Array;
      if (this.greyed) {
        if (mesh.userData.origColors) continue;
        mesh.userData.origColors = colors.slice();
        for (let i = 0; i < colors.length; i += 3) {
          c.setRGB(colors[i], colors[i + 1], colors[i + 2]).lerp(
            grey,
            RECALC_GREY_MIX
          );
          colors[i] = c.r;
          colors[i + 1] = c.g;
          colors[i + 2] = c.b;
        }
        attr.needsUpdate = true;
      } else if (mesh.userData.origColors) {
        colors.set(mesh.userData.origColors as Float32Array);
        delete mesh.userData.origColors;
        attr.needsUpdate = true;
      }
    }
  }

  /** Apply the current show flags to already-built meshes (no re-plant). */
  applyShowOptions() {
    if (this.trunkMesh) this.trunkMesh.visible = this.showStems;
    for (const mesh of this.crownMeshes) mesh.visible = this.showCanopy;
    if (this.litterMesh) this.litterMesh.visible = this.showSurface;
    if (this.logMesh) this.logMesh.visible = this.showSurface;
    if (this.nsShrubMesh) this.nsShrubMesh.visible = this.showSurface;
    if (this.grassMesh) this.grassMesh.visible = this.showGrass;
    // The crown glow/torch honours showFire through a uniform — no CPU pass.
    this.uFireOn.value = this.showFire ? 1 : 0;
    if (this.renderer) this.renderer.shadowMap.needsUpdate = true;
    this.map?.triggerRepaint();
  }

  onAdd(map: mapboxgl.Map, gl: WebGL2RenderingContext) {
    this.map = map;
    this.camera = new THREE.Camera();
    this.scene = new THREE.Scene();
    this.group = new THREE.Group();
    this.scene.add(this.group);
    this.scene.add(new THREE.HemisphereLight(0xffffff, 0x3a3024, 0.95));
    const sun = new THREE.DirectionalLight(0xfff4e0, 1.5);
    sun.position.set(-400, 700, -300);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.bias = -0.0008;
    sun.shadow.normalBias = 0.6;
    this.scene.add(sun);
    this.scene.add(sun.target);
    this.sun = sun;
    this.renderer = new THREE.WebGLRenderer({
      canvas: map.getCanvas(),
      context: gl,
    });
    this.renderer.autoClear = false;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    // The forest is completely static between rebuilds, so the shadow map is
    // rendered once per rebuild (needsUpdate on swap) instead of every map
    // frame — per-frame cost is just the shadow texture sample. Fire playback
    // deliberately does NOT touch it: shadows of burnt crowns linger, which is
    // visually negligible and saves a full shadow pass per tick.
    this.renderer.shadowMap.autoUpdate = false;
  }

  onRemove() {
    this.buildGen++; // invalidate any in-flight build
    this.clearInstances();
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    for (const resolve of this.pending.values()) resolve(null);
    this.pending.clear();
    this.dropQueued();
    this.workerBusy = false;
    this.map = null;
    this.renderer = null;
    this.scene = null;
    this.camera = null;
    this.group = null;
    this.batch = null;
    this.sun = null;
    this.ref = null;
  }

  render(_gl: WebGL2RenderingContext, matrix: number[]) {
    if (!this.map || !this.renderer || !this.scene || !this.camera || !this.ref) {
      return;
    }
    if (!this.batch || this.batch.children.length === 0) return;
    // Map matrix is mercator→clip; compose with the cached anchor transform so
    // the scene is metres, y-up, origin at `ref` (standard Mapbox three.js
    // recipe). `anchor` only changes on batch swap.
    this.camera.projectionMatrix = this.scratch
      .fromArray(matrix)
      .multiply(this.anchor);
    this.renderer.resetState();
    this.renderer.render(this.scene, this.camera);
    if (perfEnabled) {
      const now = performance.now();
      if (now - this.lastInfoSample > 500) {
        this.lastInfoSample = now;
        const info = this.renderer.info;
        p3dGauge("drawCalls", info.render.calls);
        p3dGauge("triangles", info.render.triangles);
        p3dGauge("geometries", info.memory.geometries);
        p3dGauge("textures", info.memory.textures);
      }
    }
  }

  /** Mute / restore the 2D fill ramp depending on whether trees are shown. */
  syncFillDim(dim: boolean) {
    const map = this.map;
    if (!map || !this.manageFill) return;
    try {
      if (!map.getLayer(this.sourceId)) return;
      map.setPaintProperty(
        this.sourceId,
        "fill-color",
        dim ? DIM_FILL_COLOR : FUEL_FILL_COLOR
      );
      map.setPaintProperty(
        this.sourceId,
        "fill-opacity",
        dim ? DIM_FILL_OPACITY : FUEL_FILL_OPACITY
      );
    } catch {
      /* map or fill layer mid-teardown */
    }
  }

  private disposeBatch(batch: THREE.Group) {
    for (const child of [...batch.children]) {
      batch.remove(child);
      const mesh = child as THREE.InstancedMesh;
      if (mesh.isInstancedMesh) {
        mesh.dispose();
        // Crown meshes clone the unit geometry to carry their per-instance
        // fire attributes — dispose the clone or its GPU buffers leak.
        if (mesh.userData.ownsGeometry) mesh.geometry.dispose();
        (mesh.material as THREE.Material).dispose();
      }
    }
  }

  private removeShadowGround() {
    if (!this.shadowGround) return;
    this.scene?.remove(this.shadowGround);
    this.shadowGround.geometry.dispose();
    (this.shadowGround.material as THREE.Material).dispose();
    this.shadowGround = null;
  }

  private clearInstances() {
    this.removeShadowGround();
    if (this.batch) {
      this.group?.remove(this.batch);
      this.disposeBatch(this.batch);
      this.batch = null;
    }
    this.trunkMesh = null;
    this.crownMeshes = [];
    this.litterMesh = null;
    this.logMesh = null;
    this.nsShrubMesh = null;
    this.grassMesh = null;
  }

  /** Wrap one worker bucket in an InstancedMesh — buffers upload as-is. */
  private bakeBucket(
    geoSrc: THREE.BufferGeometry,
    bucket: BucketResult,
    target: THREE.Group,
    shadow = true,
    burnable = false
  ): THREE.InstancedMesh | null {
    if (bucket.count === 0) return null;
    let geo = geoSrc;
    const opacity = this.buildOpacity;
    const mat = new THREE.MeshLambertMaterial({
      color: 0xffffff,
      transparent: opacity < 1,
      opacity,
    });
    if (burnable) {
      // Crowns carry per-instance fire state and animate entirely in-shader
      // from `uTime`: a warm firelight underglow while the cell burns
      // (strongest on the canopy underside), a residual smoulder behind the
      // front, and a vertex-collapse once the front has passed if the cell's
      // peak flames reached the crown (canopy consumed). A tick is one
      // uniform write — no instanceMatrix uploads, no CPU loop.
      geo = geoSrc.clone();
      geo.setAttribute(
        "instanceFireArrival",
        new THREE.InstancedBufferAttribute(bucket.fireArrival!, 1)
      );
      geo.setAttribute(
        "instanceFireReached",
        new THREE.InstancedBufferAttribute(bucket.fireReached!, 1)
      );
      const uniforms = {
        uGlow: this.glowUniform,
        uTime: this.uTime,
        uFireOn: this.uFireOn,
      };
      mat.onBeforeCompile = (sh) => {
        Object.assign(sh.uniforms, uniforms);
        sh.vertexShader =
          "attribute float instanceFireArrival;\n" +
          "attribute float instanceFireReached;\n" +
          "uniform float uTime;\nuniform float uFireOn;\n" +
          "varying float vFire;\nvarying float vUnder;\n" +
          sh.vertexShader
            .replace(
              "void main() {",
              "void main() {\n  vUnder = clamp(1.0 - position.y, 0.0, 1.0);"
            )
            .replace(
              "#include <begin_vertex>",
              "#include <begin_vertex>\n" +
                "  float fireAge = uTime - instanceFireArrival;\n" +
                "  float fireBurning = uFireOn * step(0.0, fireAge);\n" +
                `  float firePassed = step(${FLAME_FADE_SECONDS.toFixed(1)}, fireAge);\n` +
                // full firelight at the front, residual smoulder behind
                "  vFire = fireBurning * (1.0 - 0.5 * firePassed);\n" +
                // crowned out → canopy consumed (degenerate triangles)
                "  transformed *= 1.0 - fireBurning * instanceFireReached * firePassed;\n"
            );
        sh.fragmentShader = sh.fragmentShader
          .replace(
            "void main() {",
            "uniform float uGlow;\nvarying float vFire;\nvarying float vUnder;\nvoid main() {"
          )
          .replace(
            "#include <dithering_fragment>",
            "#include <dithering_fragment>\n" +
              "  float under = pow(vUnder, 2.4);\n" + // concentrate toward the base
              "  float glow = clamp(vFire, 0.1, 1.0) * under * uGlow;\n" +
              "  gl_FragColor.rgb += glow * vec3(1.0, 0.42, 0.12) * 1.7;"
          );
      };
    }
    // Construct at count 0 so the ctor doesn't allocate a matrix array we'd
    // immediately replace — the worker composed matrices/colours straight into
    // these buffers; wrap them, no per-instance setMatrixAt/setColorAt pass.
    const mesh = new THREE.InstancedMesh(geo, mat, 0);
    mesh.instanceMatrix = new THREE.InstancedBufferAttribute(bucket.matrices, 16);
    mesh.instanceColor = new THREE.InstancedBufferAttribute(bucket.colors, 3);
    mesh.count = bucket.count;
    // Instance bounds don't reflect per-instance transforms; skip culling.
    mesh.frustumCulled = false;
    // Grass casts no shadow — thousands of tiny blades add only shadow noise.
    mesh.castShadow = shadow;
    mesh.receiveShadow = shadow;
    // Full count for the movement LOD (setMoving lowers mesh.count, not this).
    mesh.userData.fullCount = bucket.count;
    if (burnable) mesh.userData.ownsGeometry = true;
    if (this.moving) {
      mesh.count = Math.max(1, Math.ceil(bucket.count * MOVING_FRAC));
    }
    target.add(mesh);
    return mesh;
  }

  // ----------------------------------------------------------------------
  // 3D fire animation
  // ----------------------------------------------------------------------

  /**
   * Rasterise the fetched fire cells into a grid over the view: lowest arrival
   * (seconds) and peak flame height per grid cell, then a morphological CLOSE
   * so the sparse ~20 m cells fill into a solid footprint with no holes and no
   * outward growth. Runs once per rebuild, not per tick.
   */
  private buildFireGrid(view: Bbox): FireGridData | null {
    if (!this.fireCells) return null;
    const n = FIRE_GRID_N;
    const x0 = view[0];
    const y0 = view[1];
    const dx = (view[2] - view[0]) / (n - 1) || 1e-9;
    const dy = (view[3] - view[1]) / (n - 1) || 1e-9;
    const arrival = new Float32Array(n * n).fill(Infinity);
    const flame = new Float32Array(n * n);
    const grid: FireGridData = { x0, y0, dx, dy, n, arrival, flame };
    let any = false;
    for (const c of this.fireCells) {
      if (c.mx < view[0] || c.mx > view[2] || c.my < view[1] || c.my > view[3]) {
        continue;
      }
      const idx = fireSampleIdx(grid, c.mx, c.my);
      if (c.arrival < arrival[idx]) arrival[idx] = c.arrival;
      if (c.peak > flame[idx]) flame[idx] = c.peak;
      any = true;
    }
    if (!any) return null;

    // Dilate: an empty cell inherits the lowest arrival / highest flame of its
    // 4-neighbours.
    for (let pass = 0; pass < FIRE_GRID_DILATE; pass++) {
      const srcA = arrival.slice();
      const srcF = flame.slice();
      for (let j = 0; j < n; j++) {
        for (let i = 0; i < n; i++) {
          const idx = j * n + i;
          if (Number.isFinite(srcA[idx])) continue;
          let bestA = Infinity;
          let bestF = 0;
          if (i > 0 && srcA[idx - 1] < bestA) { bestA = srcA[idx - 1]; bestF = srcF[idx - 1]; }
          if (i < n - 1 && srcA[idx + 1] < bestA) { bestA = srcA[idx + 1]; bestF = srcF[idx + 1]; }
          if (j > 0 && srcA[idx - n] < bestA) { bestA = srcA[idx - n]; bestF = srcF[idx - n]; }
          if (j < n - 1 && srcA[idx + n] < bestA) { bestA = srcA[idx + n]; bestF = srcF[idx + n]; }
          if (Number.isFinite(bestA)) {
            arrival[idx] = bestA;
            flame[idx] = bestF;
          }
        }
      }
    }
    // Erode: drop any cell touching empty ground, peeling the dilation margin
    // back off. Closing keeps the original burnt cells and the filled interior.
    for (let pass = 0; pass < FIRE_GRID_DILATE; pass++) {
      const srcA = arrival.slice();
      for (let j = 0; j < n; j++) {
        for (let i = 0; i < n; i++) {
          const idx = j * n + i;
          if (!Number.isFinite(srcA[idx])) continue;
          const edge =
            (i > 0 && !Number.isFinite(srcA[idx - 1])) ||
            (i < n - 1 && !Number.isFinite(srcA[idx + 1])) ||
            (j > 0 && !Number.isFinite(srcA[idx - n])) ||
            (j < n - 1 && !Number.isFinite(srcA[idx + n]));
          if (edge) {
            arrival[idx] = Infinity;
            flame[idx] = 0;
          }
        }
      }
    }
    return grid;
  }

  /** Set the whole-burn dataset (fetched once); null clears the fire. */
  setFireData(cells: FireCell[] | null) {
    this.fireCells = cells && cells.length ? cells : null;
  }

  /** Set the explicit crowns (with their baked ignition times); null clears them. */
  setCrowns(set: CrownSet | null) {
    this.crownSet = set && set.x.length ? set : null;
  }

  /**
   * Advance the fire to a new sim time (seconds). One uniform write + repaint
   * — the crown shader glows/torches from its baked per-instance attributes.
   */
  setFireTime(seconds: number) {
    this.uTime.value = seconds;
    this.map?.triggerRepaint();
  }

  /**
   * Lower instance counts and drop ground shadows while the camera moves;
   * restore both on settle. The shadow *map* is already frozen (autoUpdate
   * off), but the receiver samples it with PCF taps every frame — hiding it
   * removes that per-frame cost (and the visual mismatch of full-density
   * shadows under a thinned canopy).
   */
  setMoving(moving: boolean) {
    this.moving = moving;
    if (!this.batch) return;
    let changed = false;
    for (const child of this.batch.children) {
      const mesh = child as THREE.InstancedMesh;
      if (!mesh.isInstancedMesh) continue;
      const full = (mesh.userData.fullCount as number | undefined) ?? mesh.count;
      const next = moving ? Math.max(1, Math.ceil(full * MOVING_FRAC)) : full;
      if (mesh.count !== next) {
        mesh.count = next;
        changed = true;
      }
    }
    if (this.shadowGround && this.shadowGround.visible === moving) {
      this.shadowGround.visible = !moving;
      changed = true;
    }
    if (changed) this.map?.triggerRepaint();
  }

  /**
   * Shadow quality tier: full PCFSoft @ 2048² close in, cheaper PCF @ 1024²
   * when zoomed out (many more instances, each shadow far smaller on screen).
   * Materials are rebuilt every swap, so a tier change recompiles cleanly.
   */
  private setShadowTier(soft: boolean) {
    if (!this.renderer || !this.sun || this.shadowSoft === soft) return;
    this.shadowSoft = soft;
    this.renderer.shadowMap.type = soft ? THREE.PCFSoftShadowMap : THREE.PCFShadowMap;
    const size = soft ? 2048 : 1024;
    this.sun.shadow.mapSize.set(size, size);
    if (this.sun.shadow.map) {
      this.sun.shadow.map.dispose();
      this.sun.shadow.map = null;
    }
  }

  /**
   * Build the invisible shadow receiver: a grid draped over the terrain via the
   * shared elevation grid (no per-vertex raycasts), with ShadowMaterial so only
   * the trees' shadows are visible on it. Clamped to a square around the map
   * centre — at high pitch the view bbox reaches the horizon.
   */
  private buildShadowReceiver(
    view: Bbox,
    ref: { x: number; y: number; scale: number },
    grid: ElevGrid
  ) {
    const map = this.map;
    if (!map || !this.scene) return;
    const scale = ref.scale;
    const RECEIVER_MAX_M = 4096; // beyond this trees are sub-pixel anyway
    const half = (RECEIVER_MAX_M / 2) * scale;
    const x0 = Math.max(view[0], ref.x - half);
    const x1 = Math.min(view[2], ref.x + half);
    const z0 = Math.max(view[1], ref.y - half);
    const z1 = Math.min(view[3], ref.y + half);
    if (x0 >= x1 || z0 >= z1) return;
    const widthM = (x1 - x0) / scale;
    const heightM = (z1 - z0) / scale;
    const cx = ((x0 + x1) / 2 - ref.x) / scale;
    const cz = ((z0 + z1) / 2 - ref.y) / scale;
    const geo = new THREE.PlaneGeometry(
      widthM,
      heightM,
      RECEIVER_SEGMENTS,
      RECEIVER_SEGMENTS
    );
    geo.rotateX(-Math.PI / 2);
    const posAttr = geo.attributes.position;
    for (let j = 0; j < posAttr.count; j++) {
      const mx = ref.x + (cx + posAttr.getX(j)) * scale;
      const my = ref.y + (cz + posAttr.getZ(j)) * scale;
      const e = elevAt(grid, mx, my);
      posAttr.setY(j, (Number.isNaN(e) ? 0 : e) + 0.1);
    }
    geo.computeVertexNormals();
    const mesh = new THREE.Mesh(
      geo,
      // Lighter (was 0.5) so the dense grass shadows read as subtle dapple
      // rather than an overbearing dark wash across the grassland. Scaled by
      // the layer opacity so a translucent forest casts equally faint shadows.
      new THREE.ShadowMaterial({
        opacity: 0.32 * this.buildOpacity,
        depthWrite: false,
      })
    );
    mesh.position.set(cx, 0, cz);
    mesh.receiveShadow = true;
    mesh.frustumCulled = false;
    mesh.visible = !this.moving;
    this.scene.add(mesh);
    this.shadowGround = mesh;
  }

  /**
   * Aim the sun and fit its orthographic shadow camera around the planted
   * bounds. Sun sits to the south-east, fairly high — roughly agreeing with
   * the baked lighting in most satellite imagery so the two shadow systems
   * don't point in contradictory directions.
   */
  private fitSun(
    minX: number,
    maxX: number,
    minZ: number,
    maxZ: number,
    minY: number,
    maxY: number
  ) {
    if (!this.sun) return;
    const cx = (minX + maxX) / 2;
    const cz = (minZ + maxZ) / 2;
    const cy = (minY + maxY) / 2;
    const r = Math.hypot(maxX - minX, maxZ - minZ) / 2 + (maxY - minY) + 120;
    // Lower sun (smaller y) → longer shadows raking across the landscape.
    const dir = new THREE.Vector3(0.55, 0.78, 0.45).normalize();
    const dist = r * 2 + 300;
    this.sun.position.set(cx + dir.x * dist, cy + dir.y * dist, cz + dir.z * dist);
    this.sun.target.position.set(cx, cy, cz);
    this.sun.target.updateMatrixWorld();
    const cam = this.sun.shadow.camera;
    cam.left = -r;
    cam.right = r;
    cam.top = r;
    cam.bottom = -r;
    cam.near = Math.max(1, dist - r - 100);
    cam.far = dist + r + 100;
    cam.updateProjectionMatrix();
  }

  /** Buffers safe to transfer to the worker (the elev/fire grids are copied —
   *  the main thread still needs them for the receiver after the plant). */
  private requestTransferables(req: PlantRequest): ArrayBuffer[] {
    const out: ArrayBuffer[] = [];
    for (const set of req.polySets) {
      out.push(
        set.coords.buffer as ArrayBuffer,
        set.ringStarts.buffer as ArrayBuffer,
        set.polyStarts.buffer as ArrayBuffer,
        set.bboxes.buffer as ArrayBuffer
      );
    }
    if (req.quad) out.push(req.quad.buffer as ArrayBuffer);
    return out;
  }

  private postToWorker(req: PlantRequest, resolve: (r: PlantResult | null) => void) {
    this.workerBusy = true;
    this.pending.set(req.gen, resolve);
    this.worker!.postMessage(req, this.requestTransferables(req));
  }

  private dropQueued() {
    if (!this.queued) return;
    const q = this.queued;
    this.queued = null;
    q.resolve(null);
  }

  /** Run the planting in the worker (spawned lazily), inline on fallback. */
  private plant(req: PlantRequest): Promise<PlantResult | null> {
    if (!this.workerBroken) {
      try {
        if (!this.worker) {
          this.worker = new Worker(
            new URL("./fuel3d/plantingWorker.ts", import.meta.url),
            { type: "module" }
          );
          this.worker.onmessage = (e: MessageEvent<PlantResult>) => {
            const resolve = this.pending.get(e.data.gen);
            if (resolve) {
              this.pending.delete(e.data.gen);
              resolve(e.data);
            }
            this.workerBusy = false;
            const next = this.queued;
            if (next && this.worker && !this.workerBroken) {
              this.queued = null;
              this.postToWorker(next.req, next.resolve);
            }
          };
          this.worker.onerror = () => {
            // Worker unusable (blocked by CSP / bundling issue) — resolve
            // everything in flight as failed and plant inline from now on.
            this.workerBroken = true;
            const inflight = [...this.pending.values()];
            this.pending.clear();
            this.dropQueued();
            this.workerBusy = false;
            this.worker?.terminate();
            this.worker = null;
            inflight.forEach((resolve) => resolve(null));
          };
        }
        return new Promise((resolve) => {
          if (this.workerBusy) {
            if (this.queued) this.queued.resolve(null); // superseded
            this.queued = { req, resolve };
            return;
          }
          this.postToWorker(req, resolve);
        });
      } catch {
        this.workerBroken = true;
      }
    }
    try {
      return Promise.resolve(runPlanting(req));
    } catch {
      return Promise.resolve(null);
    }
  }

  /**
   * Re-plant all trees from the currently loaded fuel-load tiles.
   *
   * Main thread does only what needs the map: query features, classify them
   * (cached per feature id), flatten geometries, sample the terrain + fire
   * grids. The planting loop runs in the worker; the result is baked into a
   * hidden group and swapped in whole, so the previous forest stays visible
   * throughout (no blink-out, no settle stall).
   *
   * Resolves false when the build is incomplete (terrain tiles not yet loaded
   * under some plants, or a failed plant) so the caller keeps it scheduled.
   */
  async rebuild(): Promise<boolean> {
    const map = this.map;
    if (!map || !this.group) return true;
    const gen = ++this.buildGen;
    const zoom = map.getZoom();

    const endTotal = p3dSpan("rebuild.total");
    const endPrep = p3dSpan("rebuild.prep");

    const endFeatures = p3dSpan("rebuild.features");
    let features: GeoJSON.Feature[] = [];
    try {
      features = map.querySourceFeatures(this.sourceId, {
        sourceLayer: SOURCE_LAYER,
      }) as unknown as GeoJSON.Feature[];
    } catch {
      // Source mid-teardown (tiles URL change); next idle rebuild catches up.
      map.triggerRepaint();
      return false;
    }
    endFeatures();

    const center = mapboxgl.MercatorCoordinate.fromLngLat(map.getCenter());
    const scale = center.meterInMercatorCoordinateUnits();
    const ref = { x: center.x, y: center.y, scale };

    // Plant over the ground actually visible on screen, plus a 10% buffer, so
    // the tree budget goes to what's in frame. getBounds() can't be used: it
    // projects the screen onto a flat z=0 plane, so with camera pitch over real
    // terrain it badly under-covers the elevated foreground. map.unproject() is
    // terrain-aware in mapbox-gl v3. Sample a grid of screen points and take
    // their bbox; sky pixels at the very top (high pitch) drop out of the
    // finite set instead of skewing the box toward the horizon.
    let view: Bbox | null = null;
    const canvas = map.getCanvas();
    const vw = canvas.clientWidth;
    const vh = canvas.clientHeight;
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity,
      samples = 0;
    for (const fy of [0, 0.25, 0.5, 0.75, 1]) {
      for (const fx of [0, 0.5, 1]) {
        const ll = map.unproject([fx * vw, fy * vh]);
        if (!Number.isFinite(ll.lng) || !Number.isFinite(ll.lat)) continue;
        const mc = mapboxgl.MercatorCoordinate.fromLngLat(ll);
        if (!Number.isFinite(mc.x) || !Number.isFinite(mc.y)) continue;
        if (mc.x < minX) minX = mc.x;
        if (mc.y < minY) minY = mc.y;
        if (mc.x > maxX) maxX = mc.x;
        if (mc.y > maxY) maxY = mc.y;
        samples++;
      }
    }
    if (samples >= 3 && maxX > minX && maxY > minY) {
      const padX = (maxX - minX) * 0.1;
      const padY = (maxY - minY) * 0.1;
      view = [minX - padX, minY - padY, maxX + padX, maxY + padY];
    }
    if (!view) {
      endPrep();
      endTotal();
      map.triggerRepaint();
      return false;
    }

    // Ground-projected view quad (bl→br→tr→tl) — culls the lateral triangles
    // the padded bbox includes at high pitch (~2x the visible ground).
    let quad: Float64Array | null = null;
    {
      const corners: Array<[number, number]> = [
        [0, vh],
        [vw, vh],
        [vw, 0],
        [0, 0],
      ];
      const pts: number[] = [];
      for (const [px, py] of corners) {
        const ll = map.unproject([px, py]);
        if (!Number.isFinite(ll.lng) || !Number.isFinite(ll.lat)) break;
        const mc = mapboxgl.MercatorCoordinate.fromLngLat(ll);
        if (!Number.isFinite(mc.x) || !Number.isFinite(mc.y)) break;
        pts.push(mc.x, mc.y);
      }
      if (pts.length === 8) {
        // Pad outward from the centroid so the quad matches the bbox's buffer.
        const cx = (pts[0] + pts[2] + pts[4] + pts[6]) / 4;
        const cy = (pts[1] + pts[3] + pts[5] + pts[7]) / 4;
        quad = new Float64Array(8);
        for (let i = 0; i < 4; i++) {
          quad[i * 2] = cx + (pts[i * 2] - cx) * 1.15;
          quad[i * 2 + 1] = cy + (pts[i * 2 + 1] - cy) * 1.15;
        }
      }
    }

    // ---- classify features (cached per feature id) + flatten geometry ------
    const endClassify = p3dSpan("rebuild.classify");
    const wantGround =
      zoom >= GROUND_MIN_ZOOM && (this.showSurface || this.showGrass);
    const polySets: PolySet[] = [];
    const stands: StandInput[] = [];
    const groundStands: GroundStandInput[] = [];
    let flattenMs = 0;
    for (const feature of features) {
      const info = getFuelFeatureInfo(map, this.sourceId, feature);
      const needStand = Boolean(info.spec);
      const needGround = wantGround && Boolean(info.ground);
      if (!needStand && !needGround) continue;
      const tf = perfEnabled ? performance.now() : 0;
      const set = featureToPolySet(feature);
      if (perfEnabled) flattenMs += performance.now() - tf;
      if (!set) continue;
      const idx = polySets.push(set) - 1;
      if (needStand) stands.push({ ...info.spec!, polySet: idx });
      if (needGround) groundStands.push({ ground: info.ground!, polySet: idx });
    }
    endClassify();
    p3dRecord("rebuild.flatten", flattenMs);
    // Everything the plant needs is now in the flat request; drop the query
    // features so they aren't held across the await (tens of MB on a large view).
    features = [];

    // Sample terrain once for both the plants and the shadow receiver.
    const endElev = p3dSpan("rebuild.elevGrid");
    const elevGrid = buildElevGrid(map, view, ELEV_GRID_N);
    endElev();
    // Fire arrival/peak-flame grid (from the fetched dataset), if a fire is on.
    const endFire = p3dSpan("rebuild.fireGrid");
    const fireGrid = this.buildFireGrid(view);
    endFire();

    // Device + display budgets: smaller instance caps on low-memory devices,
    // smaller ground-cover fill budget on high-DPR displays (sub-pixel litter
    // is pure fill cost at retina resolutions).
    const deviceMem = (navigator as { deviceMemory?: number }).deviceMemory;
    const memScale = deviceMem ? clamp(deviceMem / 8, 0.5, 1) : 1;
    const dpr = window.devicePixelRatio || 1;
    const fillScale = dpr > 1.5 ? 0.5 : 1;

    // Camera ground position + altitude — the taper and far-LOD split key on
    // true 3D distance from the CAMERA, not the map centre (at high pitch the
    // centre sits kilometres ahead of the nearest, biggest-on-screen trees).
    let camX = ref.x;
    let camY = ref.y;
    let camAltM = 0;
    try {
      const camPos = map.getFreeCameraOptions().position;
      if (camPos) {
        camX = camPos.x;
        camY = camPos.y;
        camAltM = camPos.toAltitude();
      }
    } catch {
      /* free camera unavailable — fall back to the map centre, no altitude */
    }
    // Full density out to where an ~8 m crown is still ~10 px tall (f is the
    // vertical focal length in px for Mapbox's default 36.87° fov), and never
    // closer than ~1.4x the camera altitude so the nearest ground row is
    // untouched at any pitch. Only tapered when meaningfully pitched — at
    // near-nadir the screen scale is uniform and thinning would just be holes.
    const focalPx = 1.5 * vh;
    const pitched = map.getPitch() > 25;
    const taperD0 = pitched ? Math.max(0.8 * focalPx, camAltM * 1.4) : 0;
    // Low-poly crowns beyond ~12 px apparent size; altitude counts, so a
    // zoomed-out view (big altitude) goes low-poly wholesale — where crowns
    // are a handful of pixels and the facets are invisible anyway.
    const farDistM = Math.max((2 / 3) * focalPx, camAltM * 1.2);

    // Zoomed out, real-size trees are sub-pixel; scale the geometry so a
    // nominal 15 m tree keeps ~10 px of screen height (metres-per-pixel from
    // zoom + latitude). 1 (real size) from ~z15 in.
    const mpp = 1 / (512 * Math.pow(2, zoom) * scale);
    const sizeMul = Math.max(1, (TREE_MIN_PX * mpp) / NOMINAL_TREE_M);

    const req: PlantRequest = {
      gen,
      view,
      quad,
      refX: ref.x,
      refY: ref.y,
      scale,
      zoom,
      terrainOn: Boolean(map.getTerrain()),
      elev: elevGrid,
      fire: fireGrid,
      crowns: this.crownSet,
      polySets,
      stands,
      groundStands,
      wantGround,
      showSurface: this.showSurface,
      showGrass: this.showGrass,
      maxTrees: Math.round(MAX_TREES * memScale),
      maxGround: Math.round(MAX_GROUND * memScale * fillScale),
      grassClumpScale: fillScale === 1 ? 1 : 0.7,
      sizeMul,
      camX,
      camY,
      camAltM,
      taperD0,
      farDistM,
      dropFarTrunks: true,
    };
    endPrep();

    const result = await this.plant(req);
    // Superseded by a newer rebuild, or the layer was torn down mid-flight.
    if (gen !== this.buildGen || !this.map || !this.group || !this.scene) {
      return true;
    }
    if (!result) {
      endTotal();
      return false;
    }
    p3dRecord("rebuild.plant", result.plantMs);

    // ---- bake + swap (main-thread cost: attribute wrap + GPU upload) -------
    const endBake = p3dSpan("rebuild.bake");
    this.buildOpacity = layerOpacity();
    const newBatch = new THREE.Group();
    this.trunkMesh = this.bakeBucket(trunkGeo, result.trunks, newBatch);
    const coneNear = this.bakeBucket(coneGeo, result.conesNear, newBatch, true, true);
    const coneFar = this.bakeBucket(coneFarGeo, result.conesFar, newBatch, true, true);
    const blobNear = this.bakeBucket(blobGeo, result.blobsNear, newBatch, true, true);
    const blobFar = this.bakeBucket(blobFarGeo, result.blobsFar, newBatch, true, true);
    this.crownMeshes = [coneNear, coneFar, blobNear, blobFar].filter(
      (m): m is THREE.InstancedMesh => m !== null
    );
    // Ground cover casts no shadow (tiny elements add only shadow noise + cost).
    this.litterMesh = this.bakeBucket(litterGeo, result.litter, newBatch, false);
    this.logMesh = this.bakeBucket(logGeo, result.logs, newBatch, false);
    this.nsShrubMesh = this.bakeBucket(blobGeo, result.nsShrubs, newBatch, false);
    this.grassMesh = this.bakeBucket(grassGeo, result.grass, newBatch, false);
    if (this.trunkMesh) this.trunkMesh.visible = this.showStems;
    for (const mesh of this.crownMeshes) mesh.visible = this.showCanopy;
    if (this.litterMesh) this.litterMesh.visible = this.showSurface;
    if (this.logMesh) this.logMesh.visible = this.showSurface;
    if (this.nsShrubMesh) this.nsShrubMesh.visible = this.showSurface;
    if (this.grassMesh) this.grassMesh.visible = this.showGrass;
    this.uFireOn.value = this.showFire ? 1 : 0;

    // Swap: the old generation stays visible until this exact point.
    const oldBatch = this.batch;
    this.removeShadowGround();
    this.group.add(newBatch);
    this.batch = newBatch;
    this.ref = ref;
    this.anchor
      .makeTranslation(ref.x, ref.y, 0)
      .scale(new THREE.Vector3(scale, -scale, scale))
      .multiply(new THREE.Matrix4().makeRotationX(Math.PI / 2));
    if (oldBatch) {
      this.group.remove(oldBatch);
      this.disposeBatch(oldBatch);
    }

    if (result.treeCount > 0) {
      this.setShadowTier(zoom >= 16);
      this.buildShadowReceiver(view, ref, elevGrid);
      const b = result.bounds;
      this.fitSun(b[0], b[1], b[2], b[3], b[4], b[5]);
    }
    if (result.capped && !this.capWarned) {
      this.capWarned = true;
      console.debug(
        `FuelLoad3DTrees: capped at ${req.maxTrees} trees; zoom in for full density`
      );
    }
    // Rebuilds while greyed (pan/idle) bake meshes with their normal colours;
    // re-grey them so the stale-data state survives the rebuild.
    if (this.greyed) this.applyGreyed();
    // One-shot shadow render on the next frame (autoUpdate is off).
    if (this.renderer) this.renderer.shadowMap.needsUpdate = true;
    this.syncFillDim(result.treeCount > 0);
    map.triggerRepaint();
    endBake();
    endTotal();
    p3dGauge("trees", result.treeCount);
    p3dGauge("ground", result.groundCount);
    return !result.terrainMissing;
  }
}

// ---------------------------------------------------------------------------
// React wrapper — mounted through the layer's `customComponent` slot
// ---------------------------------------------------------------------------
const FuelLoad3DTrees: React.FC<FuelLoad3DTreesProps> = ({
  sourceId,
  active,
  dimFill = true,
  greyed = false,
  show,
  projectId: projectIdProp,
  onPlanted,
}) => {
  const { map } = useMap();
  const onPlantedRef = useRef(onPlanted);
  onPlantedRef.current = onPlanted;
  const { selectedIgnitionInfo, frameElapsedS } = useUrbanAnimation();
  const { currentSavedSimId } = useSimulation();
  const options = useFuel3DOptions();
  const canopy = show?.canopy ?? options.canopy;
  const stems = show?.stems ?? options.stems;
  const surface = show?.surface ?? options.surface;
  const grass = show?.grass ?? options.grass;
  const fire = options.fire;
  const layerRef = useRef<FuelLoadTreeLayer | null>(null);
  // Latest show flags, read by the attach path without re-running it.
  const showRef = useRef({ canopy, stems, fire, surface, grass });
  showRef.current = { canopy, stems, fire, surface, grass };
  // Latest greyed state, read by the attach path without re-running it.
  const greyedRef = useRef(greyed);
  greyedRef.current = greyed;

  // The whole burn is fetched ONCE (sparse per-cell arrival seconds + peak
  // flame) and the 3D fire is driven entirely from that in-memory dataset, keyed
  // on the animation's elapsed sim-seconds. Sims with no usable data get no fire.
  const partition = (selectedIgnitionInfo?.partition as string | undefined) ?? null;
  const fireEnabled = active && !!currentSavedSimId && !!partition;
  const { data: fire3d } = useFire3DData(
    currentSavedSimId ?? null,
    partition,
    fireEnabled
  );
  const elapsed = frameElapsedS ?? 0;

  // Project the fetched lng/lat cells to mercator once, only when the dataset
  // (or active state) changes.
  const fireMercCells = useMemo<FireCell[] | null>(() => {
    const cells = fire3d?.data?.cells;
    if (!active || !cells || cells.length === 0) return null;
    return cells.map(([lng, lat, arrival, peak]) => {
      const m = mapboxgl.MercatorCoordinate.fromLngLat({ lng, lat });
      return { mx: m.x, my: m.y, arrival, peak };
    });
  }, [fire3d, active]);

  // Latest dataset + time, read by the attach path without re-running it.
  const fireRef = useRef<{ cells: FireCell[] | null; time: number }>({
    cells: null,
    time: 0,
  });
  fireRef.current = { cells: fireMercCells, time: elapsed };

  // The project's crowns (one per tree / shrub, at its imagery position),
  // projected to mercator once, with each crown's ignition time baked from
  // the selected run: graph nodes get the time the urban graph torched them
  // (or never), clump interiors sample the fire grid like a lattice tree.
  const { projectId: routeProjectId } = useParams<{ projectId: string }>();
  const projectId =
    projectIdProp === undefined ? routeProjectId : projectIdProp ?? undefined;
  const { data: crownsData } = useCrowns(projectId, active);
  const crownIgnition = selectedIgnitionInfo?.urban_crown_ignition_s;
  const crownSet = useMemo<CrownSet | null>(() => {
    if (!active || !crownsData || crownsData.count === 0) return null;
    const n = crownsData.count;
    const set: CrownSet = {
      x: new Float64Array(n),
      y: new Float64Array(n),
      radiusM: new Float32Array(n),
      heightM: new Float32Array(n),
      kind: new Uint8Array(n),
      fireArrival: new Float32Array(n),
    };
    for (let i = 0; i < n; i++) {
      const m = mapboxgl.MercatorCoordinate.fromLngLat({
        lng: crownsData.lng[i],
        lat: crownsData.lat[i],
      });
      set.x[i] = m.x;
      set.y[i] = m.y;
      set.radiusM[i] = crownsData.radius_m[i];
      set.heightM[i] = crownsData.height_m[i];
      set.kind[i] = crownsData.kind[i];
      if (crownsData.role[i] === CROWN_ROLE_INTERIOR) {
        set.fireArrival[i] = CROWN_FIRE_FROM_GRID;
      } else {
        const t = crownIgnition?.[String(crownsData.id[i])];
        set.fireArrival[i] = t != null && Number.isFinite(t) ? t : FIRE_NEVER;
      }
    }
    return set;
  }, [crownsData, crownIgnition, active]);
  const crownRef = useRef<CrownSet | null>(null);
  crownRef.current = crownSet;

  useEffect(() => {
    if (!map || !active) return;

    let cancelled = false;
    let sourceDirty = true;
    let lastViewKey = "";
    let rebuildTimer: number | null = null;
    let rebuildToken = 0;
    const layerId = `${sourceId}-3d-trees`;

    const rebuildIfChanged = () => {
      const layer = layerRef.current;
      if (!layer || cancelled) return;
      const c = map.getCenter();
      const viewKey = `${map.getZoom().toFixed(2)}|${c.lng.toFixed(5)}|${c.lat.toFixed(5)}`;
      if (!sourceDirty && viewKey === lastViewKey) return;
      lastViewKey = viewKey;
      sourceDirty = false;
      // The rebuild is async (worker planting) — an incomplete build (DEM
      // tiles still loading under some trees) re-marks the source dirty so the
      // next idle/sourcedata pass finishes the job. Stale completions (a newer
      // rebuild was requested meanwhile) must not clear the newer state.
      const token = ++rebuildToken;
      void layer.rebuild().then((complete) => {
        if (cancelled || token !== rebuildToken) return;
        if (!complete) sourceDirty = true;
        onPlantedRef.current?.(complete);
      });
    };

    // `idle` never fires while an animated canvas source (AnimationLayer) is
    // playing, so a debounced moveend/sourcedata path covers that case too.
    const scheduleRebuild = () => {
      if (rebuildTimer !== null) window.clearTimeout(rebuildTimer);
      rebuildTimer = window.setTimeout(() => {
        rebuildTimer = null;
        rebuildIfChanged();
      }, 250);
    };

    const onSourceData = (e: { sourceId?: string; isSourceLoaded?: boolean }) => {
      if (
        (e.sourceId === sourceId || e.sourceId === DEM_SOURCE_ID) &&
        e.isSourceLoaded
      ) {
        sourceDirty = true;
        scheduleRebuild();
      }
    };

    // Movement LOD: thin instances while the camera moves (pan/zoom/rotate all
    // fire movestart/moveend), restore the full count when it settles.
    const onMoveStart = () => layerRef.current?.setMoving(true);
    const onMoveEnd = () => layerRef.current?.setMoving(false);

    const attach = () => {
      if (cancelled) return;
      if (!map.getLayer(layerId)) {
        const layer = new FuelLoadTreeLayer(layerId, sourceId);
        layer.setManageFill(dimFill);
        layerRef.current = layer;
        map.addLayer(layer);
      }
      // Seed the layer with the current fire dataset + time + show flags before
      // the first rebuild so it bakes everything in the same pass.
      const s = showRef.current;
      layerRef.current?.setShowOptions(
        s.canopy,
        s.stems,
        s.fire,
        s.surface,
        s.grass
      );
      layerRef.current?.setFireData(fireRef.current.cells);
      layerRef.current?.setFireTime(fireRef.current.time);
      layerRef.current?.setCrowns(crownRef.current);
      layerRef.current?.setGreyed(greyedRef.current);
      map.on("idle", rebuildIfChanged);
      map.on("moveend", scheduleRebuild);
      map.on("sourcedata", onSourceData);
      map.on("movestart", onMoveStart);
      map.on("moveend", onMoveEnd);
      rebuildIfChanged();
    };

    // Attach once the style is ready. `map.once("idle", attach)` alone can
    // strand here — on a static map `idle` may not fire again until the user
    // moves, leaving the trees invisible until then. Wait on `render` too and
    // nudge a frame so attachment resolves without interaction.
    const attachWhenReady = () => {
      if (cancelled || !map.isStyleLoaded()) return;
      map.off("idle", attachWhenReady);
      map.off("render", attachWhenReady);
      attach();
    };

    if (map.isStyleLoaded()) {
      attach();
    } else {
      map.on("idle", attachWhenReady);
      map.on("render", attachWhenReady);
      map.triggerRepaint();
    }

    return () => {
      cancelled = true;
      if (rebuildTimer !== null) window.clearTimeout(rebuildTimer);
      map.off("idle", attachWhenReady);
      map.off("render", attachWhenReady);
      map.off("idle", rebuildIfChanged);
      map.off("moveend", scheduleRebuild);
      map.off("sourcedata", onSourceData);
      map.off("movestart", onMoveStart);
      map.off("moveend", onMoveEnd);
      // Restore the saturated fill ramp before tearing the tree layer down.
      layerRef.current?.syncFillDim(false);
      try {
        if (map.getLayer(layerId)) map.removeLayer(layerId);
      } catch {
        /* map may be tearing down */
      }
      layerRef.current = null;
    };
  }, [map, active, sourceId, dimFill]);

  // New fire dataset → hand it to the layer and rebuild so crowns bake their
  // arrival seconds + peak flame as instanced attributes.
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.setFireData(fireMercCells);
    layer.setFireTime(elapsed);
    void layer.rebuild();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fireMercCells]);

  // New crowns (or a run with different crown ignitions) → re-plant so they
  // bake their positions and ignition times as instanced attributes.
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.setCrowns(crownSet);
    void layer.rebuild();
  }, [crownSet]);

  // Advance the 3D fire as the animation's elapsed time changes — one uniform.
  useEffect(() => {
    layerRef.current?.setFireTime(elapsed);
  }, [elapsed]);

  // Grey out / restore the stand as the recalculating state flips — a colour
  // swap on the built meshes, no re-plant.
  useEffect(() => {
    layerRef.current?.setGreyed(greyed);
  }, [greyed]);

  // Apply the canopy/stems/fire toggles without a full re-plant — those meshes
  // are always baked, so toggling is a visibility flip (fire is a uniform).
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.setShowOptions(canopy, stems, fire, surface, grass);
    layer.applyShowOptions();
  }, [canopy, stems, fire]); // eslint-disable-line react-hooks/exhaustive-deps

  // Surface / grass toggles gate whether the ground layer is built at all (it
  // can be hundreds of thousands of instances), so turning one on needs a
  // re-plant rather than a visibility flip. attach() already builds the ground
  // for the initial flags, so only an actual change triggers a re-plant.
  const lastGround = useRef({ surface, grass });
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.setShowOptions(canopy, stems, fire, surface, grass);
    const prev = lastGround.current;
    if (prev.surface !== surface || prev.grass !== grass) {
      lastGround.current = { surface, grass };
      void layer.rebuild();
    }
  }, [surface, grass]); // eslint-disable-line react-hooks/exhaustive-deps

  return active && perfEnabled ? <PerfHud /> : null;
};

export default FuelLoad3DTrees;
